import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Class } from '@/types/models';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, CreditCard, AlertTriangle, ExternalLink } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCanUseAutoBilling } from '@/hooks/useStripeConnectStatus';
import { enrollWithBilling } from '@/services/enrollment/enrollWithBilling';

interface AddEnrollmentDialogProps {
  studentId?: string;
  children: React.ReactNode;
}

const AddEnrollmentDialog: React.FC<AddEnrollmentDialogProps> = ({ studentId, children }) => {
  const [open, setOpen] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [autoBillingEnabled, setAutoBillingEnabled] = useState(false);
  const [autoBillingAmount, setAutoBillingAmount] = useState('');
  const [billingDay, setBillingDay] = useState('1');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = user?.user_metadata?.organization_id;
  
  // Check Stripe Connect status for auto-billing availability
  const { canUseAutoBilling, isLoading: stripeLoading, stripeStatus } = useCanUseAutoBilling();

  const { data: classes, isLoading } = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true);
        
      if (error) throw error;
      return (data || []).map(item => ({
        id: item.id,
        name: item.name,
        organization_id: item.organization_id,
        description: item.description || '',
        max_students: item.max_students,
        location_id: item.location_id,
        dress_code: item.dress_code,
        level: item.level,
        price_cents: item.price_cents,
        season_id: item.season_id,
        is_active: item.is_active,
        is_event: item.is_event,
        event_date: item.event_date,
        event_start_time: item.event_start_time,
        event_end_time: item.event_end_time,
        created_at: item.created_at
      })) as Class[];
    },
    enabled: !!organizationId && open,
  });

  const { data: existingEnrollments = [], isLoading: isLoadingEnrollments } = useQuery({
    queryKey: ['studentEnrollments-v2', studentId],
    queryFn: async () => {
      if (!studentId) return [];
      
      const { data, error } = await supabase
        .from('enrollments')
        .select('class_id')
        .eq('student_id', studentId)
        .eq('status', 'active');
        
      if (error) throw error;
      
      // Return just the class_id strings for filtering
      return data.map(enrollment => enrollment.class_id).filter(Boolean);
    },
    enabled: !!studentId && open,
  });

  const handleEnroll = async (classId: string) => {
    if (!studentId || !organizationId) {
      toast.error('Missing required information');
      return;
    }

    if (existingEnrollments.includes(classId)) {
      toast.error('Student is already enrolled in this class');
      return;
    }

    if (autoBillingEnabled && (!autoBillingAmount || parseFloat(autoBillingAmount) <= 0)) {
      toast.error('Please enter a valid billing amount');
      return;
    }

    if (autoBillingEnabled && !canUseAutoBilling) {
      toast.error('Auto-billing is not available for your organization. Please set up Stripe Connect first.');
      return;
    }

    setIsEnrolling(true);
    try {
      // Use the safe enrollment function that handles capacity and waitlist automatically
      const { data: enrollmentResult, error: enrollmentError } = await supabase
        .rpc('manage_enrollment_with_waitlist', {
          p_class_id: classId,
          p_student_id: studentId,
          p_organization_id: organizationId,
          p_enrolled_by: user?.id || null
        });

      if (enrollmentError) throw enrollmentError;

      const result = enrollmentResult as {
        success: boolean;
        status: string;
        message: string;
        enrollment_id?: string;
        waitlist_entry_id?: string;
        queue_position?: number;
      };

      if (!result.success) {
        throw new Error(result.message);
      }

      // Handle waitlist case
      if (result.status === 'waitlisted') {
        toast.success('⏰ Student Successfully Added to Waitlist', {
          description: `Class is full. Student has been added to the waitlist at position ${result.queue_position}. They will be automatically notified when a spot opens up.`,
          duration: Infinity,
          action: {
            label: 'Manage Waitlist',
            onClick: () => {
              // Close dialog and user can navigate to waitlist management
              setOpen(false);
            }
          }
        });
        // Still invalidate queries and reset form
        queryClient.invalidateQueries({ queryKey: ['studentEnrollments-v2', studentId] });
        queryClient.invalidateQueries({ queryKey: ['studentEnrollments', studentId] });
        queryClient.invalidateQueries({ queryKey: ['student', studentId] });
        queryClient.invalidateQueries({ queryKey: ['students'] });
        queryClient.invalidateQueries({ queryKey: ['classes'] });
        
        setSelectedClassId(null);
        setAutoBillingEnabled(false);
        setAutoBillingAmount('');
        setBillingDay('1');
        setOpen(false);
        return;
      }

      // If enrollment was successful, get the enrollment data
      let enrollment = null;
      if (result.status === 'enrolled' && result.enrollment_id) {
        const { data: enrollmentData, error: fetchError } = await supabase
          .from('enrollments')
          .select('*')
          .eq('id', result.enrollment_id)
          .single();

        if (fetchError) throw fetchError;
        enrollment = enrollmentData;
      }

      if (!enrollment) {
        throw new Error('Failed to retrieve enrollment data');
      }

      // If auto-billing is enabled and available, create the billing schedule
      if (autoBillingEnabled && canUseAutoBilling && enrollment) {
        const amountCents = Math.round(parseFloat(autoBillingAmount) * 100);
        const billingDayNum = parseInt(billingDay);
        
        // Calculate next billing date
        const today = new Date();
        const nextBillingDate = new Date(today.getFullYear(), today.getMonth(), billingDayNum);
        
        // If billing day has passed this month, move to next month
        if (nextBillingDate <= today) {
          nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
        }

        const { error: billingError } = await supabase
          .from('billing_schedules')
          .insert({
            enrollment_id: enrollment.id,
            organization_id: organizationId,
            amount_cents: amountCents,
            billing_day: billingDayNum,
            next_billing_date: nextBillingDate.toISOString().split('T')[0],
            status: 'active',
            description: `Monthly tuition for ${classes?.find(c => c.id === classId)?.name || 'class'}`
          });

        if (billingError) {
          // Rollback enrollment since billing failed
          await supabase.from('enrollments').delete().eq('id', enrollment.id);
          
          console.error('Error creating billing schedule:', billingError);
          toast.error('Billing setup failed. Enrollment cancelled. Please try again.');
        } else {
          toast.success('✅ Student Enrolled with Auto-Billing', {
            description: `Student has been successfully enrolled and auto-billing is set up for $${autoBillingAmount} on the ${billingDay}${billingDay === '1' ? 'st' : billingDay === '2' ? 'nd' : billingDay === '3' ? 'rd' : 'th'} of each month.`,
            duration: Infinity,
            action: {
              label: 'View Billing',
              onClick: () => {
                // Close dialog so user can navigate to billing
                setOpen(false);
              }
            }
          });
        }
      } else {
        toast.success('✅ Student Successfully Enrolled', {
          description: `Student has been enrolled in ${classes?.find(c => c.id === classId)?.name || 'the selected class'}. The family will receive a confirmation notification.`,
          duration: Infinity,
          action: {
            label: 'View Class',
            onClick: () => {
              // Close dialog so user can navigate to class details
              setOpen(false);
            }
          }
        });
      }
      
      // Invalidate relevant queries to refresh the UI immediately
      queryClient.invalidateQueries({ queryKey: ['studentEnrollments-v2', studentId] });
      queryClient.invalidateQueries({ queryKey: ['studentEnrollments', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student', studentId] });
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({ queryKey: ['billing-schedules'] });
      
      // Reset form
      setSelectedClassId(null);
      setAutoBillingEnabled(false);
      setAutoBillingAmount('');
      setBillingDay('1');
      setOpen(false);
    } catch (error: unknown) {
      console.error('Error enrolling student:', error);
      
      const errorMessage = error && typeof error === 'object' && 'message' in error 
        ? (error as { message: string }).message 
        : 'Failed to enroll student';
      
      const errorCode = error && typeof error === 'object' && 'code' in error 
        ? (error as { code: string }).code 
        : null;
      
      if (errorCode === '23505' && errorMessage.includes('enrollments_class_id_student_id_key')) {
        toast.error('Student is already enrolled in this class');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsEnrolling(false);
    }
  };

  const availableClasses = classes?.filter(cls => !existingEnrollments.includes(cls.id)) || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enroll in Class</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto py-4">
          {(isLoading || isLoadingEnrollments) ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : availableClasses.length > 0 ? (
            <div className="space-y-6">
              {/* Step 1: Class Selection */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-gray-700">Select a class:</h3>
                {availableClasses.map((cls) => (
                  <Card 
                    key={cls.id} 
                    className={`overflow-hidden cursor-pointer transition-colors ${
                      selectedClassId === cls.id ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedClassId(cls.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-medium">{cls.name}</h3>
                          {cls.level && (
                            <p className="text-sm text-muted-foreground">Level: {cls.level}</p>
                          )}
                          {cls.price_cents && (
                            <p className="text-sm text-green-600 font-medium">
                              ${(cls.price_cents / 100).toFixed(2)}
                            </p>
                          )}
                        </div>
                        <div className={`w-4 h-4 rounded-full border-2 transition-colors ${
                          selectedClassId === cls.id 
                            ? 'bg-primary border-primary' 
                            : 'border-gray-300'
                        }`} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Step 2: Auto-Billing Options */}
              {selectedClassId && (
                <div className="space-y-4 border-t pt-4">
                  {/* Stripe Connect Status Check */}
                  {stripeLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Checking billing capabilities...</span>
                    </div>
                  ) : canUseAutoBilling ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="auto-billing" className="text-sm font-medium">
                          Enable Auto-Billing
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Automatically charge monthly tuition
                        </p>
                      </div>
                      <Switch
                        id="auto-billing"
                        checked={autoBillingEnabled}
                        onCheckedChange={setAutoBillingEnabled}
                      />
                    </div>
                  ) : (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-2">
                          <p className="font-medium">Auto-billing not available</p>
                          <p className="text-sm">
                            {stripeStatus?.status === 'not_connected' 
                              ? 'Your organization needs to set up Stripe Connect to enable automatic billing.'
                              : stripeStatus?.status === 'pending'
                              ? 'Your Stripe Connect setup is still pending. Complete the setup to enable auto-billing.'
                              : 'There is an issue with your Stripe Connect setup. Contact support for assistance.'
                            }
                          </p>
                          {stripeStatus?.status === 'not_connected' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => window.open('/admin/settings?tab=payments', '_blank')}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Set up Stripe Connect
                            </Button>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {autoBillingEnabled && (
                    <div className="space-y-4 pl-4 border-l-2 border-blue-100">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="amount" className="text-sm">Monthly Amount</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <Input
                              id="amount"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={autoBillingAmount}
                              onChange={(e) => setAutoBillingAmount(e.target.value)}
                              className="pl-8"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="billing-day" className="text-sm">Billing Day</Label>
                          <Select value={billingDay} onValueChange={setBillingDay}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                                <SelectItem key={day} value={day.toString()}>
                                  {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of month
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
                        <CreditCard className="h-3 w-3" />
                        <span>Next billing: {billingDay}{parseInt(billingDay) === 1 ? 'st' : parseInt(billingDay) === 2 ? 'nd' : parseInt(billingDay) === 3 ? 'rd' : 'th'} of next month</span>
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={() => handleEnroll(selectedClassId)}
                    disabled={isEnrolling || (autoBillingEnabled && canUseAutoBilling && !autoBillingAmount)}
                    className="w-full"
                  >
                    {isEnrolling ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    {autoBillingEnabled && canUseAutoBilling ? 'Enroll with Auto-Billing' : 'Enroll Student'}
                  </Button>
                </div>
              )}
            </div>
          ) : classes && classes.length > 0 ? (
            <p className="text-center text-muted-foreground">Student is already enrolled in all available classes</p>
          ) : (
            <p className="text-center text-muted-foreground">No classes available</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddEnrollmentDialog;
